import React, { useCallback, useContext, useEffect, useState } from "react";

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
} from "@mui/material";

import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";

import { api } from "@api/api";
import { CondicaoContext } from "../../contexto";

import CatalogoModelosLista from "./componentes/catalogoModelosLista";
import ModalGerenciarCatalogos from "./componentes/modalGerenciadorCatalogos";
import ModalNovoModelo from "./componentes/modalNovoModelo";

import {
  ajustarTamanhoFonteSre,
  tamanhosControlesGlobais,
  tamanhosPaginasAdministrativas,
} from "../../estilos/global";
import TransicaoPagina from "../../componentes/comuns/TransicaoPagina";

const ModelosCatalogo = () => {
  const { role } = useContext(CondicaoContext);
  const isSuperintendent = role === "superintendent";
  const visualPadronizado = isSuperintendent || role === "analyst";

  const [modelos, setModelos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [assuntos, setAssuntos] = useState([]);
  const [etapasModalidades, setEtapasModalidades] = useState([]);

  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState("");
  const [erroAcao, setErroAcao] = useState("");

  const [modalCatalogosAberto, setModalCatalogosAberto] = useState(false);
  const [modalModeloAberto, setModalModeloAberto] = useState(false);
  const [modeloEditando, setModeloEditando] = useState(null);

  const [modeloParaExcluir, setModeloParaExcluir] = useState(null);
  const [excluindoModelo, setExcluindoModelo] = useState(false);

  const lerMensagemErro = async (response, mensagemPadrao) => {
    const texto = await response.text();

    if (!texto) {
      return mensagemPadrao;
    }

    try {
      const dados = JSON.parse(texto);

      if (Array.isArray(dados?.message)) {
        return dados.message.join(" ");
      }

      return dados?.message || mensagemPadrao;
    } catch {
      return texto || mensagemPadrao;
    }
  };

  const ordenarModelos = (lista) =>
    [...lista].sort((a, b) => {
      const categoria = String(a.categoria_nome ?? "").localeCompare(
        String(b.categoria_nome ?? ""),
        "pt-BR",
      );

      if (categoria !== 0) {
        return categoria;
      }

      const assunto = String(a.assunto_nome ?? "").localeCompare(
        String(b.assunto_nome ?? ""),
        "pt-BR",
      );

      if (assunto !== 0) {
        return assunto;
      }

      return String(a.titulo ?? "").localeCompare(
        String(b.titulo ?? ""),
        "pt-BR",
      );
    });

  const carregarCatalogo = useCallback(async ({ silencioso = false } = {}) => {
    if (!silencioso) {
      setLoading(true);
    }

    setErro("");

    try {
      const token = localStorage.getItem("token");

      const opcoes = {
        headers: {
          Authorization: token ? `Bearer ${token}` : "",
        },
        credentials: "include",
      };

      const [
        respostaModelos,
        respostaCategorias,
        respostaAssuntos,
        respostaEtapasModalidades,
      ] = await Promise.all([
        fetch(api.listModels(), opcoes),
        fetch(api.listModelCategories(), opcoes),
        fetch(api.listModelSubjects(), opcoes),
        fetch(api.listModelStagesModalities(), opcoes),
      ]);

      if (
        !respostaModelos.ok ||
        !respostaCategorias.ok ||
        !respostaAssuntos.ok ||
        !respostaEtapasModalidades.ok
      ) {
        throw new Error("Erro ao carregar o catálogo.");
      }

      const [
        dadosModelos,
        dadosCategorias,
        dadosAssuntos,
        dadosEtapasModalidades,
      ] = await Promise.all([
        respostaModelos.json(),
        respostaCategorias.json(),
        respostaAssuntos.json(),
        respostaEtapasModalidades.json(),
      ]);

      setModelos(
        ordenarModelos(Array.isArray(dadosModelos) ? dadosModelos : []),
      );

      setCategorias(Array.isArray(dadosCategorias) ? dadosCategorias : []);

      setAssuntos(Array.isArray(dadosAssuntos) ? dadosAssuntos : []);

      setEtapasModalidades(
        Array.isArray(dadosEtapasModalidades) ? dadosEtapasModalidades : [],
      );
    } catch (error) {
      console.error("Erro ao carregar catálogo:", error);

      setErro("Não foi possível carregar o catálogo de modelos.");
    } finally {
      if (!silencioso) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    carregarCatalogo();
  }, [carregarCatalogo]);

  const handleDownload = async (modelo) => {
    setErroAcao("");

    try {
      const token = localStorage.getItem("token");

      const response = await fetch(api.downloadModel(modelo.id), {
        method: "GET",
        headers: {
          Authorization: token ? `Bearer ${token}` : "",
        },
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error(
          await lerMensagemErro(response, "Não foi possível baixar o modelo."),
        );
      }

      const blob = await response.blob();
      const urlTemporaria = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = urlTemporaria;
      link.download = modelo.nome_original || modelo.titulo || "modelo";

      document.body.appendChild(link);
      link.click();
      link.remove();

      URL.revokeObjectURL(urlTemporaria);
    } catch (error) {
      console.error("Erro ao baixar modelo:", error);

      setErroAcao(error.message || "Não foi possível baixar o modelo.");
    }
  };

  const handleAbrirNovoModelo = () => {
    setErroAcao("");
    setModeloEditando(null);
    setModalModeloAberto(true);
  };

  const handleAbrirEdicao = (modelo) => {
    if (!isSuperintendent) {
      return;
    }

    setErroAcao("");
    setModeloEditando(modelo);
    setModalModeloAberto(true);
  };

  const handleFecharModalModelo = () => {
    setModalModeloAberto(false);
    setModeloEditando(null);
  };

  const handleSalvarModelo = async ({
    id,
    titulo,
    categoriaId,
    assuntoId,
    etapasIds,
    modalidadesIds,
    arquivo,
    editando,
  }) => {
    const token = localStorage.getItem("token");
    const formData = new FormData();

    formData.append("titulo", titulo);
    formData.append("categoria_id", String(categoriaId));

    if (editando) {
      formData.append("assunto_id", assuntoId ? String(assuntoId) : "");
    } else if (assuntoId) {
      formData.append("assunto_id", String(assuntoId));
    }

    formData.append("etapas_ids", JSON.stringify(etapasIds ?? []));

    formData.append("modalidades_ids", JSON.stringify(modalidadesIds ?? []));

    if (arquivo) {
      formData.append("arquivo", arquivo);
    }

    const endpoint = editando ? api.updateModel(id) : api.createModel();

    const metodo = editando ? "PATCH" : "POST";

    const mensagemErro = editando
      ? "Não foi possível atualizar o modelo."
      : "Não foi possível cadastrar o modelo.";

    const response = await fetch(endpoint, {
      method: metodo,
      headers: {
        Authorization: token ? `Bearer ${token}` : "",
      },
      credentials: "include",
      body: formData,
    });

    if (!response.ok) {
      throw new Error(await lerMensagemErro(response, mensagemErro));
    }

    await carregarCatalogo({ silencioso: true });
  };

  const handleAbrirExclusao = (modelo) => {
    if (!isSuperintendent) {
      return;
    }

    setErroAcao("");
    setModeloParaExcluir(modelo);
  };

  const handleFecharExclusao = () => {
    if (!excluindoModelo) {
      setModeloParaExcluir(null);
    }
  };

  const handleConfirmarExclusao = async () => {
    if (!modeloParaExcluir || excluindoModelo) {
      return;
    }

    setExcluindoModelo(true);
    setErroAcao("");

    try {
      const token = localStorage.getItem("token");

      const response = await fetch(api.deleteModel(modeloParaExcluir.id), {
        method: "DELETE",
        headers: {
          Authorization: token ? `Bearer ${token}` : "",
        },
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error(
          await lerMensagemErro(response, "Não foi possível excluir o modelo."),
        );
      }

      setModeloParaExcluir(null);
      await carregarCatalogo({ silencioso: true });
    } catch (error) {
      console.error("Erro ao excluir modelo:", error);

      setErroAcao(error.message || "Não foi possível excluir o modelo.");
    } finally {
      setExcluindoModelo(false);
    }
  };

  if ((isSuperintendent || role === "user") && loading) {
    return (
      <Box
        sx={{
          minHeight: "60vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const conteudo = (
    <Container
      maxWidth={
        isSuperintendent || role === "analyst" || role === "user" ? false : "lg"
      }
      disableGutters={isSuperintendent || role === "analyst" || role === "user"}
      sx={{ py: visualPadronizado ? 0 : 2.5 }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: { xs: "flex-start", md: "center" },
          justifyContent: "space-between",
          flexDirection: { xs: "column", md: "row" },
          gap: 2,
          mb: 1.5,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: visualPadronizado ? "center" : "flex-start",
            gap: visualPadronizado ? 1.4 : 1.25,
          }}
        >
          <Box
            sx={{
              width: visualPadronizado ? 44 : 42,
              height: visualPadronizado ? 44 : 42,
              borderRadius: 1.5,
              border: visualPadronizado ? "none" : "1px solid #d8e9f8",
              backgroundColor: visualPadronizado ? "#e8f4ff" : "#eef7ff",
              color: "#087cf0",
              display: "grid",
              placeItems: "center",
              flexShrink: 0,
            }}
          >
            <DescriptionOutlinedIcon
              sx={{ fontSize: visualPadronizado ? 27 : 24 }}
            />
          </Box>

          <Box sx={visualPadronizado ? { minWidth: 0 } : undefined}>
            <Typography
              component={visualPadronizado ? "h1" : undefined}
              variant="h5"
              sx={{
                color: "#0767d8",
                fontSize: visualPadronizado
                  ? tamanhosPaginasAdministrativas.tituloPagina
                  : undefined,
                fontWeight: 700,
                lineHeight: 1.15,
              }}
            >
              Modelos e roteiros
            </Typography>

            <Typography
              variant={visualPadronizado ? "body1" : "caption"}
              color="text.secondary"
              sx={{
                display: "block",
                mt: visualPadronizado ? 0.3 : 0.35,
                color: visualPadronizado ? "#7b8798" : undefined,
                fontSize: visualPadronizado
                  ? tamanhosControlesGlobais.descricao
                  : ajustarTamanhoFonteSre("0.73rem"),
                lineHeight: visualPadronizado ? 1.35 : undefined,
              }}
            >
              {isSuperintendent
                ? "Gerencie os modelos e as classificações disponíveis para a SRE."
                : "Consulte e baixe os modelos disponíveis para utilização nos processos."}
            </Typography>
          </Box>
        </Box>

        {isSuperintendent && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Button
              variant="outlined"
              size="small"
              startIcon={<SettingsRoundedIcon />}
              onClick={() => setModalCatalogosAberto(true)}
              sx={{
                minHeight: 36,
                px: 1.75,
                textTransform: "none",
                borderColor: "#9fcdff",
                color: "#0767d8",
                fontSize: tamanhosControlesGlobais.botao,
                fontWeight: 600,
                boxShadow: "none",
                "&:hover": {
                  borderColor: "#087cf0",
                  backgroundColor: "#f5faff",
                  boxShadow: "none",
                },
              }}
            >
              Gerenciar catálogos
            </Button>

            <Button
              variant="contained"
              size="small"
              startIcon={<AddRoundedIcon />}
              onClick={handleAbrirNovoModelo}
              sx={{
                minHeight: 36,
                px: 1.75,
                textTransform: "none",
                fontSize: tamanhosControlesGlobais.botao,
                fontWeight: 600,
                boxShadow: "none",
                "&:hover": { boxShadow: "none" },
              }}
            >
              Novo modelo
            </Button>
          </Box>
        )}
      </Box>

      {loading ? (
        <Box
          sx={{
            minHeight: 240,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress />
        </Box>
      ) : erro ? (
        <Alert severity="error">{erro}</Alert>
      ) : (
        <>
          {erroAcao && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {erroAcao}
            </Alert>
          )}

          <CatalogoModelosLista
            isSuperintendent={isSuperintendent}
            visualPadronizado={visualPadronizado}
            modelos={modelos}
            categorias={categorias}
            assuntos={assuntos}
            etapasModalidades={etapasModalidades}
            onDownload={handleDownload}
            onEditar={handleAbrirEdicao}
            onExcluir={handleAbrirExclusao}
          />
        </>
      )}

      {isSuperintendent && (
        <ModalGerenciarCatalogos
          open={modalCatalogosAberto}
          onClose={() => setModalCatalogosAberto(false)}
          categorias={categorias}
          assuntos={assuntos}
          etapasModalidades={etapasModalidades}
          onAlteracao={() => carregarCatalogo({ silencioso: true })}
        />
      )}

      {isSuperintendent && (
        <ModalNovoModelo
          open={modalModeloAberto}
          onClose={handleFecharModalModelo}
          categorias={categorias}
          assuntos={assuntos}
          etapasModalidades={etapasModalidades}
          modelo={modeloEditando}
          onSalvar={handleSalvarModelo}
        />
      )}

      <Dialog
        open={Boolean(modeloParaExcluir)}
        onClose={handleFecharExclusao}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: 2,
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            color: "#263238",
          }}
        >
          Excluir modelo
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ pt: 2.5 }}>
          <Typography variant="body1" sx={{ color: "#37474f" }}>
            Deseja realmente excluir o modelo{" "}
            <strong>{modeloParaExcluir?.titulo}</strong>?
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Esta ação removerá o arquivo do catálogo.
          </Typography>
        </DialogContent>

        <Divider />

        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button
            variant="outlined"
            onClick={handleFecharExclusao}
            disabled={excluindoModelo}
            sx={{ textTransform: "none" }}
          >
            Cancelar
          </Button>

          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmarExclusao}
            disabled={excluindoModelo}
            sx={{
              textTransform: "none",
              boxShadow: "none",
              "&:hover": { boxShadow: "none" },
            }}
          >
            {excluindoModelo ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "Excluir"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );

  return isSuperintendent || role === "user" ? (
    <TransicaoPagina variante="navegacao">{conteudo}</TransicaoPagina>
  ) : (
    conteudo
  );
};

export default ModelosCatalogo;
